import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUSINESS_WA = '972539406051';

// Wheel layout — precision SVG wheel (7 exactly-equal segments) styled after
// the user's gold reference: polished copper + black onyx + jeweled gold rim.
const SEG_ANGLE = 360 / 7;
const SEGMENTS = [
  { code: 'p90',   lines: ['90 טבליות', 'מתנה'], kind: 'copper' },
  { code: 'd20',   lines: ['20% הנחה'],           kind: 'black'  },
  { code: 'd10',   lines: ['10% הנחה'],           kind: 'copper' },
  { code: 'retry', lines: ['נסה שנית'],           kind: 'black'  },
  { code: 'p60',   lines: ['60 טבליות', 'מתנה'], kind: 'copper' },
  { code: 'd100',  lines: ['100 ש"ח', 'הנחה'],   kind: 'black'  },
  { code: 'sad',   lines: ['😢'],                  kind: 'black'  },
].map((s, i) => ({ ...s, center: i * SEG_ANGLE + SEG_ANGLE / 2 }));

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const WheelSVG = () => {
  const C = 200, R = 170;
  return (
    <svg viewBox="0 0 400 400" className="w-full h-full select-none" draggable="false">
      <defs>
        <linearGradient id="rimGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff2b3" /><stop offset="0.3" stopColor="#f5c542" />
          <stop offset="0.55" stopColor="#8a6112" /><stop offset="0.8" stopColor="#f7d774" />
          <stop offset="1" stopColor="#b8860b" />
        </linearGradient>
        <radialGradient id="copper" cx="50%" cy="42%" r="65%">
          <stop offset="0" stopColor="#e8a366" /><stop offset="0.45" stopColor="#b06a35" />
          <stop offset="1" stopColor="#6f3d14" />
        </radialGradient>
        <radialGradient id="onyx" cx="50%" cy="42%" r="65%">
          <stop offset="0" stopColor="#3d3835" /><stop offset="0.5" stopColor="#1c1917" />
          <stop offset="1" stopColor="#080706" />
        </radialGradient>
        <radialGradient id="hubGold" cx="38%" cy="30%" r="80%">
          <stop offset="0" stopColor="#fff7d6" /><stop offset="0.45" stopColor="#f5c542" />
          <stop offset="1" stopColor="#8a6112" />
        </radialGradient>
        <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#ffedb0" stopOpacity="0.95" />
          <stop offset="1" stopColor="#ffedb0" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="gloss" x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.22" />
          <stop offset="0.45" stopColor="#ffffff" stopOpacity="0.04" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.18" />
        </linearGradient>
      </defs>

      {/* segments */}
      {SEGMENTS.map((s, i) => {
        const a0 = i * SEG_ANGLE, a1 = (i + 1) * SEG_ANGLE;
        const [x0, y0] = polar(C, C, R, a0);
        const [x1, y1] = polar(C, C, R, a1);
        return (
          <path key={s.code} d={`M${C},${C} L${x0},${y0} A${R},${R} 0 0 1 ${x1},${y1} Z`}
            fill={s.kind === 'copper' ? 'url(#copper)' : 'url(#onyx)'} />
        );
      })}
      {/* brushed-metal fine lines on copper segments */}
      {SEGMENTS.map((s, i) => s.kind === 'copper' && Array.from({ length: 10 }).map((_, j) => {
        const a = i * SEG_ANGLE + 4 + j * ((SEG_ANGLE - 8) / 9);
        const [x0, y0] = polar(C, C, 52, a);
        const [x1, y1] = polar(C, C, R - 4, a);
        return <line key={`${s.code}-b${j}`} x1={x0} y1={y0} x2={x1} y2={y1} stroke="#7d4517" strokeOpacity="0.28" strokeWidth="1" />;
      }))}
      {/* gold separators + tip beads */}
      {SEGMENTS.map((_, i) => {
        const [x, y] = polar(C, C, R, i * SEG_ANGLE);
        const [bx, by] = polar(C, C, R - 2, i * SEG_ANGLE);
        return (
          <g key={`sep-${i}`}>
            <line x1={C} y1={C} x2={x} y2={y} stroke="#f5c542" strokeWidth="2.6" />
            <line x1={C} y1={C} x2={x} y2={y} stroke="#fff2b3" strokeWidth="0.9" />
            <circle cx={bx} cy={by} r="4.5" fill="url(#hubGold)" stroke="#8a6112" strokeWidth="0.8" />
          </g>
        );
      })}
      {/* glossy light sweep */}
      <circle cx={C} cy={C} r={R} fill="url(#gloss)" />

      {/* jeweled gold rim */}
      <circle cx={C} cy={C} r="183" fill="none" stroke="url(#rimGold)" strokeWidth="26" />
      <circle cx={C} cy={C} r="196" fill="none" stroke="#8a6112" strokeWidth="2.5" />
      <circle cx={C} cy={C} r="170.5" fill="none" stroke="#fde68a" strokeWidth="2" />
      <circle cx={C} cy={C} r="177" fill="none" stroke="#6b4a0d" strokeWidth="1" strokeOpacity="0.7" />
      <circle cx={C} cy={C} r="189" fill="none" stroke="#fff2b3" strokeWidth="1" strokeOpacity="0.8" />
      {/* rim beads */}
      {Array.from({ length: 28 }).map((_, i) => {
        const [x, y] = polar(C, C, 183, i * (360 / 28));
        return <circle key={`bead-${i}`} cx={x} cy={y} r="3.4" fill="url(#hubGold)" stroke="#6b4a0d" strokeWidth="0.7" />;
      })}

      {/* center medallion with gift */}
      <circle cx={C} cy={C} r="58" fill="url(#hubGlow)" />
      <circle cx={C} cy={C} r="46" fill="#1a1206" stroke="url(#rimGold)" strokeWidth="7" />
      <circle cx={C} cy={C} r="39" fill="none" stroke="#fde68a" strokeWidth="1.2" strokeDasharray="2.5 3" />
    </svg>
  );
};

// Labels are plain HTML (not SVG text) — iOS Safari breaks Hebrew RTL
// inside rotated SVG <text>. HTML bidi renders correctly everywhere.
const LABELS = [
  { code: 'p90',   text: '90 טבליות\nמתנה' },
  { code: 'd20',   text: '20% הנחה' },
  { code: 'd10',   text: '10% הנחה' },
  { code: 'retry', text: 'נסה שנית' },
  { code: 'p60',   text: '60 טבליות\nמתנה' },
  { code: 'd100',  text: '100 ש"ח\nהנחה' },
  { code: 'sad',   text: '😢' },
].map((l, i) => ({ ...l, center: i * SEG_ANGLE + SEG_ANGLE / 2 }));

const PRIZE_TEXT = {
  p90: '90 טבליות מתנה',
  p60: '60 טבליות מתנה',
  sad: 'הפעם לא זכית 😢',
};
const PRIZE_CAPTION = {
  p90: 'ברכישת 90 טבליות - קבלת מתנה 90 טבליות',
  p60: 'ברכישת 90 טבליות - קבלת מתנה 60 טבליות',
};

const WA_MESSAGES = {
  p90: `*השתתפתי בהגרלה*\nאשמח לרכוש את המבצע 90 טבליות ב349 ש"ח \nולקבל את הזכייה שלי 90 טבליות מתנה! 🎁\n\nקוד הזכייה הוא BRTE2000\n*ידוע לי שההטבה ל24 שעות בלבד*`,
  p60: `*השתתפתי בהגרלה*\nאשמח לרכוש את המבצע 90 טבליות ב349 ש"ח \nולקבל את הזכייה שלי 60 טבליות מתנה! 🎁\n\nקוד הזכייה הוא BRTE2000\n*ידוע לי שההטבה ל24 שעות בלבד*`,
};

const CONFETTI_COLORS = ['#fbbf24', '#f59e0b', '#22c55e', '#ef4444', '#3b82f6', '#e879f9', '#ffffff'];

function deviceId() {
  let id = localStorage.getItem('wheel_device_id');
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    localStorage.setItem('wheel_device_id', id);
  }
  return id;
}

// 2-second radial burst from the center of the screen
const Confetti = () => (
  <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden" data-testid="wheel-confetti">
    <style>{`@keyframes wheelBurst {0%{transform:translate(0,0) rotate(0);opacity:1}100%{transform:translate(var(--tx),var(--ty)) rotate(540deg);opacity:0}}`}</style>
    {Array.from({ length: 70 }).map((_, i) => {
      const angle = (i * 137.5) % 360;
      const dist = 130 + (i % 7) * 55;
      const tx = Math.cos(angle * Math.PI / 180) * dist;
      const ty = Math.sin(angle * Math.PI / 180) * dist;
      return (
        <span
          key={`cf-${i}`}
          style={{
            position: 'absolute', top: '50%', left: '50%',
            width: i % 3 === 0 ? 12 : 8, height: i % 2 === 0 ? 14 : 8,
            borderRadius: i % 4 === 0 ? '50%' : '2px',
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            '--tx': `${tx}px`, '--ty': `${ty}px`,
            animation: `wheelBurst ${1.4 + (i % 5) * 0.12}s ease-out forwards`,
          }}
        />
      );
    })}
  </div>
);

// The real wheel sound extracted from the user's reference video,
// time-stretched to exactly 8s to match the spin duration.
function playTicks() {
  try {
    const audio = new Audio('/sounds/wheel.mp3');
    audio.volume = 1.0;
    audio.play().catch(() => {});
  } catch (e) { /* audio blocked — non-critical */ }
}

export default function Wheel() {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null); // prize code after animation
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [burst, setBurst] = useState(false);
  const pendingPrize = useRef(null);

  // Phone comes from the WhatsApp link (?p=05xxxxxxx) — no manual input.
  // ?test=1 = free-play mode: unlimited spins, nothing saved.
  const qs = new URLSearchParams(window.location.search);
  const phoneFromLink = qs.get('p') || '';
  const isTest = qs.get('test') === '1';
  const testWin = qs.get('win') || null; // force outcome in test mode
  // The sender's WhatsApp number (?from=9725... or local 05x) — winner is
  // sent straight back to whoever sent them the link (e.g. a FlowUp CRM
  // client). Local numbers are normalized to international for wa.me.
  let waTarget = (qs.get('from') || BUSINESS_WA).replace(/\D/g, '');
  if (waTarget.startsWith('0')) waTarget = '972' + waTarget.slice(1);

  useEffect(() => {
    if (isTest) { localStorage.removeItem('wheel_prize'); return; }
    const prev = localStorage.getItem('wheel_prize');
    if (prev) { setResult(prev); setAlreadyPlayed(true); }
  }, [isTest]);

  const spin = async () => {
    if (spinning) return;
    try {
      const r = await axios.post(`${API}/wheel/spin`, { phone: phoneFromLink, device_id: deviceId(), test: isTest, test_win: isTest ? testWin : null });
      const { prize, already_played } = r.data;
      if (already_played) {
        localStorage.setItem('wheel_prize', prize);
        setResult(prize); setAlreadyPlayed(true);
        return;
      }
      pendingPrize.current = prize;
      const seg = SEGMENTS.find(s => s.code === prize);
      const target = 360 * 6 + (360 - seg.center);
      setSpinning(true);
      playTicks(8000);
      setRotation(prev => prev + target + (prev % 360 === 0 ? 0 : 360 - (prev % 360)));
      setTimeout(() => {
        setSpinning(false);
        if (prize === 'retry') {
          toast.info('נסה שנית! מגיע לך עוד סיבוב 🎡');
        } else {
          // Only real wins lock the attempt; "sad" offers another try.
          if (!isTest && prize !== 'sad') localStorage.setItem('wheel_prize', prize);
          setResult(prize);
          if (prize !== 'sad') {
            setBurst(true);
            setTimeout(() => setBurst(false), 2000);
          }
        }
      }, 8300);
    } catch (e) {
      toast.error('שגיאה — נסה שוב');
    }
  };

  const waShare = () => {
    const txt = WA_MESSAGES[result] || '*השתתפתי בהגרלה*';
    // Straight to the number the link was sent from — no share sheet.
    window.open(`https://wa.me/${waTarget}?text=${encodeURIComponent(txt)}`, '_blank');
  };

  const isTablets = result === 'p90' || result === 'p60';

  return (
    <div dir="rtl" className="min-h-screen text-white flex flex-col items-center px-4 py-8 relative overflow-hidden"
      style={{
        background: 'url(/wheel-assets/bg.jpg) center/cover no-repeat, radial-gradient(circle at 50% -10%, #a8842e 0%, #55400f 55%, #2e2206 100%)',
        fontFamily: 'Heebo, sans-serif',
      }}>

      {/* golden sparkle field */}
      <style>{`
        @keyframes twinkle {0%,100%{opacity:.15;transform:scale(.7)}50%{opacity:.95;transform:scale(1.15)}}
        @keyframes goldShine {0%{background-position:0% 50%}100%{background-position:200% 50%}}
      `}</style>
      <div className="pointer-events-none absolute inset-0">
        {Array.from({ length: 34 }).map((_, i) => (
          <span key={`sp-${i}`} className="absolute text-amber-200"
            style={{
              top: `${(i * 61) % 100}%`, left: `${(i * 37) % 100}%`,
              fontSize: i % 4 === 0 ? 14 : 9,
              animation: `twinkle ${2 + (i % 5) * 0.7}s ease-in-out ${(i % 7) * 0.4}s infinite`,
              textShadow: '0 0 6px #fde68a',
            }}>✦</span>
        ))}
      </div>

      <h1 className="text-[30px] leading-tight font-black mb-1 text-center whitespace-nowrap"
        style={{
          background: 'linear-gradient(90deg,#8a6112,#fde68a,#f5c542,#fff7d6,#c9962c)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          animation: 'goldShine 3.5s linear infinite',
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.55))',
        }}
        data-testid="wheel-title">גלגל המזל - מבצעים שווים</h1>
      <p className="text-amber-100/80 text-sm mb-6 text-center">של בריאות הגבר — סובבו את הגלגל וזכו במתנות!</p>

      {/* Wheel — the real disc image from the reference video */}
      <div className="relative mb-8" data-testid="wheel-container">
        {/* diamond pointer */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 w-8 h-8 rotate-45"
          style={{
            background: 'linear-gradient(135deg,#fff7d6 0%,#f5c542 45%,#8a6112 100%)',
            boxShadow: '0 0 14px rgba(253,230,138,.9), 0 2px 4px rgba(0,0,0,.6)',
            border: '1.5px solid #fde68a',
          }} />
        <div
          className="relative"
          style={{
            width: 'min(88vw, 380px)', height: 'min(88vw, 380px)',
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 8s cubic-bezier(0.15, 0.9, 0.25, 1)' : 'none',
            filter: 'drop-shadow(0 0 40px rgba(245,197,66,.45))',
          }}
          data-testid="wheel-disc"
        >
          <WheelSVG />
          {LABELS.map((l) => (
            <div key={`lb-${l.code}`} className="absolute inset-0 pointer-events-none"
              style={{ transform: `rotate(${l.center}deg)` }}>
              <span dir="rtl"
                className={`absolute text-center font-black text-white ${l.code === 'sad' ? 'text-[36px]' : 'text-[18px]'} leading-[1.12] whitespace-pre-line`}
                style={{
                  top: '11.5%', left: '50%', width: '32%',
                  transform: 'translateX(-50%)',
                  textShadow: '0 2px 4px #000, 0 0 10px rgba(0,0,0,.8)',
                }}>
                {l.text}
              </span>
            </div>
          ))}
        </div>
        {/* static center gift — does NOT rotate with the wheel */}
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <span style={{ fontSize: 'min(9vw, 38px)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,.6))' }}>🎁</span>
        </div>
      </div>

      {/* Spin button (no phone input — identified via the WhatsApp link) */}
      {!result && (
        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={spin}
            disabled={spinning}
            className="w-full py-4 rounded-xl font-black text-lg disabled:opacity-60 transition-all active:scale-95"
            style={{
              background: 'linear-gradient(135deg,#fff2b3,#f5c542 45%,#b8860b)',
              color: '#3a2a05',
              boxShadow: '0 4px 20px rgba(245,197,66,.45), inset 0 1px 0 rgba(255,255,255,.6)',
              border: '1px solid #fde68a',
            }}
            data-testid="wheel-spin-btn"
          >
            {spinning ? 'בהצלחה... 🤞' : 'סובב את הגלגל! 🎡'}
          </button>
          <p className="text-[11px] text-amber-100/60 text-center">ניסיון אחד לכל משתתף</p>
        </div>
      )}

      {burst && <Confetti />}

      {/* Result popup */}
      {result && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-5" data-testid="wheel-result-popup">
          <div className="bg-gradient-to-b from-stone-900 to-stone-950 border-2 border-amber-500/60 rounded-3xl p-6 max-w-sm w-full text-center space-y-4">
            {result === 'sad' ? (
              <>
                <div className="text-6xl">😢</div>
                <h2 className="text-2xl font-black text-stone-200">הפעם לא זכית</h2>
                <p className="text-amber-300 font-bold text-lg leading-snug" data-testid="wheel-sad-retry-text">
                  אנחנו מרגישים שזה יום המזל שלך<br />נסה שוב!
                </p>
              </>
            ) : (
              <>
                <div className="text-4xl">🎉</div>
                <h2 className="text-2xl font-black text-amber-400" data-testid="wheel-prize-title">ברכותיי! זכית ב:</h2>
                <p className="text-xl font-bold text-white">{PRIZE_TEXT[result]}</p>
                {isTablets && (
                  <div className="bg-white rounded-2xl p-3">
                    <img src="/products/back2game.png" alt="Back 2 Game" className="h-[264px] mx-auto object-contain" data-testid="wheel-prize-image" />
                    <p className="text-stone-900 font-black text-base mt-1" data-testid="wheel-prize-caption">
                      {PRIZE_CAPTION[result]}
                    </p>
                    <p className="text-red-600 font-bold text-sm mt-1" data-testid="wheel-prize-expiry">
                      מימוש ההטבה הינו ל24 שעות בלבד מרגע הזכייה
                    </p>
                  </div>
                )}
                {alreadyPlayed && (
                  <p className="text-[30px] leading-tight font-black text-amber-300" data-testid="wheel-already-played">
                    כבר השתתפת בהגרלה — זה הפרס שלך 🙂
                  </p>
                )}
              </>
            )}
            {result !== 'sad' && (
              <button
                onClick={waShare}
                className="w-full py-3.5 rounded-xl font-black text-white text-base flex items-center justify-center gap-2 active:scale-95 transition-all"
                style={{ background: 'linear-gradient(135deg,#25d366,#128c7e)' }}
                data-testid="wheel-wa-btn"
              >
                💬 לחץ כאן ושלח אלינו את הפרס שזכית
              </button>
            )}
            {result === 'sad' && (
              <button
                onClick={() => setResult(null)}
                className="w-full py-3.5 rounded-xl font-black text-stone-950 text-base active:scale-95 transition-all"
                style={{ background: 'linear-gradient(135deg,#fbbf24,#d97706)' }}
                data-testid="wheel-sad-retry-btn"
              >
                נסה שוב 🎡
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
